import { Button } from 'streamlabs-beaker';
import StudioModal from '@/components/modals/StudioModal.vue';
import CheckoutMixin from './CheckoutMixin';
import { Component, Mixins } from 'vue-property-decorator';
import { EModalName } from '@/helpers/modal';
import {
  ESubscriptionPlanId,
  ESubscriptionPlanType,
  ISubscription,
} from '@/helpers/subscription';
import NotificationHelper from '@/helpers/notification';
import SubscriptionApi from '@/apis/subscription-api';
import { capitalize, cloneDeep } from 'lodash';
import UtilHelper from '@/helpers/utils';

@Component({
  components: {
    StudioModal,
    SButton: Button,
  },
})
export default class SwitchPlanModal extends Mixins(CheckoutMixin) {
  switchProcessing: boolean = false;
  isSwitched: boolean = false;
  previousSubscription: ISubscription = null;
  newSubscription: ISubscription = null;

  get shouldFullScreen(): boolean {
    return window.innerWidth < 490;
  }

  get isSwitchPlanToMonthly(): boolean {
    return this.selectedPlan === ESubscriptionPlanType.monthly;
  }

  get modalName(): string {
    return EModalName.switchPlan;
  }

  get title(): string {
    if (this.isSwitchPlanToMonthly) return 'You are currently on the best value plan';

    return 'Switch to an Annual Plan to save more';
  }

  get subtitle(): string {
    if (this.isSwitchPlanToMonthly) return 'You are currently on the best value plan. Are you sure you want to switch to a monthly plan?';

    return 'Update your Console Pro plan from Monthly to Annual for a better value.';
  }

  get confirmSwitchButtonText(): string {
    if (this.isSwitchPlanToMonthly) return 'Switch to Monthly Plan';

    return 'Switch to Annual Plan';
  }

  get closeModalText(): string {
    if (this.isSwitched) return 'Close';
    if (this.isSwitchPlanToMonthly) return 'Keep current subscription';

    return 'Keep current subscription';
  }

  get newSubscriptionPlan(): string {
    if (!this.userSubscription) return '';

    return capitalize(this.userSubscription.plan_type);
  }

  get newSubscriptionPrice(): string {
    if (!this.userSubscription) return '';
    const period: string =
      this.userSubscription.plan_type === ESubscriptionPlanId.annual ? 'year' : 'month';

    return `$${(this.plans[this.userSubscription.plan_type].price / 100).toFixed(2)}/${period}`;
  }

  get previousSubscriptionExpiresAt(): string {
    if (!this.previousSubscription) return '';

    return UtilHelper.dateToHumanReadable(this.previousSubscription.expires_at);
  }

  async switchPlan() {
    if (this.isSwitched) {
      this.$modal.hide(this.modalName);
      return;
    }

    this.previousSubscription = cloneDeep(this.userSubscription);
    this.switchProcessing = true;

    const planId = this.plans[this.selectedPlan].planId;
    const response = await SubscriptionApi.resubscribe({
      planId,
    });

    if (!response.success) {
      NotificationHelper.error(response.message);
      return;
    }

    this.userSubscription = response.subscription;

    this.switchProcessing = false;
    this.isSwitched = true;
  }

  async beforeOpen({ params }) {
    if (params?.plan) {
      this.selectedPlan = params.plan;

      if (!this.isSwitchPlanToMonthly) this.switchPlan();
    } else {
      this.selectedPlan = this.userSubscription?.plan_type === ESubscriptionPlanType.monthly ?
        ESubscriptionPlanType.annual : ESubscriptionPlanType.monthly;
    }
  }

  closed() {
    this.resetSelectedPlan();
    this.resetValues();
  }

  resetValues() {
    this.switchProcessing = false;
    this.isSwitched = false;
    this.previousSubscription = null;
    this.newSubscription = null;
  }

  hideModal() {
    this.$modal.hide(this.modalName);
  }

  onPlanSelect() {
    if (this.selectedPlan === ESubscriptionPlanType.annual) this.switchPlan();
  }
}
